// import React from "react";
import Decimal from "decimal.js-light";

import { useReconciledPayment } from "../../Order/ReconciledPayment";
import { useGetDiscountValue } from "../Voucher/useGetDiscount";
import {
  getSum,
  getNonCancelledItems,
  getMaxZeroD,
} from "../../helpers/methods";
import { useGetCreditPayment } from "../Credit/hooks";

//this is clone function between FE UI
const calculateTaxExclWithShippingTax = (props) => {
  const {
    orderItems = [],
    merchandiseTotal,
    shippingDecimal,
    discountForItems = 0,
    discountForShipping = 0,
    taxRate,
    shippingRate,
    totalReconciledPaidAmount,
    totalAppliedCreditDecimal,
    deductableCreditD,
  } = props;

  const itemsWithDiscount = [
    ...orderItems,
    ...(new Decimal(discountForItems).greaterThan(0)
      ? [
          {
            description: "Discount",
            price: new Decimal(discountForItems).negated(),
            quantity: 1,
          },
        ]
      : []),
  ];

  const shippingsWithDiscount = [
    { price: shippingDecimal, description: "Shipping" },
    ...(discountForShipping
      ? [
          {
            price: new Decimal(discountForShipping).negated(),
            description: "Shipping Discount",
          },
        ]
      : []),
  ];

  const itemAfterDiscount = merchandiseTotal.minus(discountForItems);
  const itemTaxedBase = getMaxZeroD(itemAfterDiscount.toNumber());
  const shippingAfterDiscount = shippingDecimal.minus(discountForShipping);
  const shippingTaxedBase = getMaxZeroD(shippingAfterDiscount.toNumber());
  const totalAfterDiscount = itemAfterDiscount.plus(shippingAfterDiscount);

  // Apply tax per line for items
  const itemTaxValues = itemsWithDiscount.map((item) => {
    const price = new Decimal(item.price);
    const tax = price
      .times(taxRate)
      .dividedBy(100)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    return {
      beforeTax: price,
      tax,
    };
  });

  const itemBeforeTax = itemTaxValues.reduce(
    (sum, t) => sum.plus(t.beforeTax),
    new Decimal(0)
  );
  const itemTax = itemTaxValues.reduce(
    (sum, t) => sum.plus(t.tax),
    new Decimal(0)
  );

  // Apply tax per line for shipping
  const shippingTaxValues = shippingsWithDiscount.map((s) => {
    const price = new Decimal(s.price);
    const tax = price
      .times(shippingRate)
      .dividedBy(100)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    return {
      beforeTax: price,
      tax,
    };
  });

  const shippingBeforeTax = shippingTaxValues.reduce(
    (sum, t) => sum.plus(t.beforeTax),
    new Decimal(0)
  );
  const shippingTax = shippingTaxValues.reduce(
    (sum, t) => sum.plus(t.tax),
    new Decimal(0)
  );

  const itemAfterTax = itemBeforeTax.plus(itemTax);
  const shippingAfterTax = shippingBeforeTax.plus(shippingTax);
  const totalBeforeTax = itemBeforeTax.plus(shippingBeforeTax);
  const taxAmount = itemTax.plus(shippingTax);
  const totalAfterTax = itemAfterTax.plus(shippingAfterTax);
  const balanceDecimal = totalAfterDiscount
    .plus(taxAmount)
    .minus(totalReconciledPaidAmount)
    .minus(totalAppliedCreditDecimal)
    .minus(deductableCreditD);

  return {
    // Item-related
    itemAfterDiscount,
    itemBeforeTax,
    itemTax,
    itemAfterTax,

    // Shipping-related
    shippingAfterDiscount,
    shippingBeforeTax,
    shippingTax,
    shippingAfterTax,

    // Payment-related
    totalReconciledPaidAmount,
    totalCreditUsed: totalAppliedCreditDecimal,
    outstandingPayment: balanceDecimal,

    // Final totals
    taxableAmount: itemTaxedBase.plus(shippingTaxedBase),
    totalBeforeTax,
    taxAmount,
    totalAfterTax,
  };
};

export const useTaxExclWithShippingTax = ({
  record = {},
  taxRate = 0,
  shippingRate = 0,
}) => {
  const { payments = [], orderItems = [], shippingCost = 0 } = record;
  const { discountForItems, discountForShipping } = useGetDiscountValue(record);
  const totalReconciledPaidAmount = useReconciledPayment(payments);
  const totalCreditPayment = useGetCreditPayment(payments);

  const items = orderItems.filter(getNonCancelledItems);
  const merchandiseTotal = new Decimal(getSum(items));
  const shippingDecimal = isNaN(parseInt(shippingCost))
    ? new Decimal(0)
    : new Decimal(shippingCost);
  const totalCredit = new Decimal(totalCreditPayment);

  const result = calculateTaxExclWithShippingTax({
    orderItems: items,
    merchandiseTotal,
    shippingDecimal,
    discountForItems,
    discountForShipping,
    taxRate,
    shippingRate,
    totalReconciledPaidAmount,
    totalAppliedCreditDecimal: totalCredit,
    deductableCreditD: new Decimal(0),
  });
  return {
    // Item-related
    itemAfterDiscount: result.itemAfterDiscount.toFixed(2),
    itemBeforeTax: result.itemBeforeTax.toFixed(2),
    itemTax: result.itemTax.toFixed(2),
    itemAfterTax: result.itemAfterTax.toFixed(2),

    // Shipping-related
    shippingAfterDiscount: result.shippingAfterDiscount.toFixed(2),
    shippingBeforeTax: result.shippingBeforeTax.toFixed(2),
    shippingTax: result.shippingTax.toFixed(2),
    shippingAfterTax: result.shippingAfterTax.toFixed(2),

    // Payment-related
    totalReconciledPaidAmount: result.totalReconciledPaidAmount,
    totalCreditUsed: result.totalCreditUsed.toFixed(2),
    outstandingPayment: result.outstandingPayment.toFixed(2),

    // Final totals
    taxableAmount: result.taxableAmount.toFixed(2),
    totalBeforeTax: result.totalBeforeTax.toFixed(2),
    taxAmount: result.taxAmount.toFixed(2),
    totalAfterTax: result.totalAfterTax.toFixed(2),

    // Original gross values for UI/reference
    merchandiseTotal: merchandiseTotal.toFixed(2),
    shippingCost: shippingDecimal.toFixed(2),
  };
};

export const getTaxExclWithShippingTax = useTaxExclWithShippingTax;

//this is clone function between FE UI
const calculateTaxExclWithoutShippingTax = (props) => {
  const {
    orderItems = [],
    merchandiseTotal,
    shippingDecimal,
    discountForItems = 0,
    discountForShipping = 0,
    taxRate,
    totalReconciledPaidAmount,
    totalAppliedCreditDecimal,
    deductableCreditD,
  } = props;

  const itemsWithDiscount = [
    ...orderItems,
    ...(new Decimal(discountForItems).greaterThan(0)
      ? [
          {
            description: "Discount",
            price: new Decimal(discountForItems).negated(),
            quantity: 1,
          },
        ]
      : []),
  ];

  const itemAfterDiscount = merchandiseTotal.minus(discountForItems);
  const itemTaxedBase = getMaxZeroD(itemAfterDiscount.toNumber());
  const shippingAfterDiscount = shippingDecimal.minus(discountForShipping);
  const shippingTaxedBase = getMaxZeroD(shippingAfterDiscount.toNumber());
  const totalAfterDiscount = itemAfterDiscount.plus(shippingAfterDiscount);

  // Apply tax per line for items
  const itemTaxValues = itemsWithDiscount.map((item) => {
    const price = new Decimal(item.price);
    const tax = price
      .times(taxRate)
      .dividedBy(100)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    return {
      beforeTax: price,
      tax,
    };
  });

  const itemBeforeTax = itemTaxValues.reduce(
    (sum, t) => sum.plus(t.beforeTax),
    new Decimal(0)
  );
  const itemTax = itemTaxValues.reduce(
    (sum, t) => sum.plus(t.tax),
    new Decimal(0)
  );

  const itemAfterTax = itemBeforeTax.plus(itemTax);
  const shippingBeforeTax = shippingTaxedBase;
  const shippingTax = new Decimal(0);
  const shippingAfterTax = shippingTaxedBase;

  const totalBeforeTax = itemBeforeTax.plus(shippingBeforeTax);
  const taxAmount = itemTax;
  const totalAfterTax = itemAfterTax.plus(shippingAfterTax);

  const balanceDecimal = totalAfterDiscount
    .plus(taxAmount)
    .minus(totalReconciledPaidAmount)
    .minus(totalAppliedCreditDecimal)
    .minus(deductableCreditD);

  return {
    // Item-related
    itemAfterDiscount,
    itemBeforeTax,
    itemTax,
    itemAfterTax,

    // Shipping-related
    shippingAfterDiscount,
    shippingBeforeTax,
    shippingTax,
    shippingAfterTax,

    // Payment-related
    totalReconciledPaidAmount,
    totalCreditUsed: totalAppliedCreditDecimal,
    outstandingPayment: balanceDecimal,

    // Final totals
    taxableAmount: itemTaxedBase,
    totalBeforeTax,
    taxAmount,
    totalAfterTax,
  };
};

export const useTaxExclWithoutShippingTax = ({
  record = {},
  taxRate = 0,
  shippingRate = 0,
}) => {
  const { payments = [], orderItems = [], shippingCost = 0 } = record;
  const { discountForItems, discountForShipping } = useGetDiscountValue(record);
  const totalReconciledPaidAmount = useReconciledPayment(payments);
  const totalCreditPayment = useGetCreditPayment(payments);

  const items = orderItems.filter(getNonCancelledItems);
  const merchandiseTotal = new Decimal(getSum(items));
  const shippingDecimal = isNaN(parseInt(shippingCost))
    ? new Decimal(0)
    : new Decimal(shippingCost);
  const totalCredit = new Decimal(totalCreditPayment);

  const result = calculateTaxExclWithoutShippingTax({
    orderItems: items,
    merchandiseTotal,
    shippingDecimal,
    discountForItems,
    discountForShipping,
    taxRate,
    shippingRate,
    totalReconciledPaidAmount,
    totalAppliedCreditDecimal: totalCredit,
    deductableCreditD: new Decimal(0),
  });

  return {
    // Item-related
    itemAfterDiscount: result.itemAfterDiscount.toFixed(2),
    itemBeforeTax: result.itemBeforeTax.toFixed(2),
    itemTax: result.itemTax.toFixed(2),
    itemAfterTax: result.itemAfterTax.toFixed(2),

    // Shipping-related
    shippingAfterDiscount: result.shippingAfterDiscount.toFixed(2),
    shippingBeforeTax: result.shippingBeforeTax.toFixed(2),
    shippingTax: result.shippingTax.toFixed(2),
    shippingAfterTax: result.shippingAfterTax.toFixed(2),

    // Payment-related
    totalReconciledPaidAmount: result.totalReconciledPaidAmount,
    totalCreditUsed: result.totalCreditUsed.toFixed(2),
    outstandingPayment: result.outstandingPayment.toFixed(2),

    // Final totals
    taxableAmount: result.taxableAmount.toFixed(2),
    totalBeforeTax: result.totalBeforeTax.toFixed(2),
    taxAmount: result.taxAmount.toFixed(2),
    totalAfterTax: result.totalAfterTax.toFixed(2),

    // Raw totals for reference (if needed)
    merchandiseTotal: merchandiseTotal.toFixed(2),
    shippingCost: shippingDecimal.toFixed(2),
  };
};

export const getTaxExclWithoutShippingTax = useTaxExclWithoutShippingTax;
